import { calcCrow } from "./utils";
import type { Competitor } from "../types/competitors";

/**
 * The portal's one door onto the Google Places (New) JS library that
 * `public/index.html` loads with `libraries=places`. The library is untyped
 * here (no @types/google.maps), so the surface is deliberately small and
 * nothing else should reach for `window.google` directly.
 */

/** The browser key `public/index.html` loads Maps with — the Places photo
 *  endpoint wants it on the URL. Keep the two in step. */
const MAPS_BROWSER_KEY = "AIzaSyDE-xYVEPC8f9MVR11jl7CWO7bFRrNp2KA";

/** What a competitor row is built from — the fields the customer picker
 *  requests, so what an operator adds has the shape of what a customer adds. */
const COMPETITOR_FIELDS = [
  "displayName",
  "location",
  "photos",
  "formattedAddress",
];

/** Each suggestion costs a details call to fill in the fields above. */
const MAX_SUGGESTIONS = 6;

export interface LatLng {
  lat: number;
  lng: number;
}

function placesLibrary(): any {
  return (window as any).google?.maps?.places ?? null;
}

export function isPlacesAvailable(): boolean {
  return typeof placesLibrary()?.Place?.searchByText === "function";
}

/**
 * A competitor's photo is stored the way the customer picker stores it — the
 * Places photo resource name (`places/…/photos/…`), which only renders through
 * the media endpoint. Rows from the older picker hold a full URL instead.
 */
export function competitorImageUrl(imageUrl: string | undefined): string | null {
  if (!imageUrl) return null;
  if (/^https?:\/\//.test(imageUrl)) return imageUrl;
  return `https://places.googleapis.com/v1/${imageUrl}/media?maxHeightPx=400&maxWidthPx=400&key=${MAPS_BROWSER_KEY}`;
}

function photoReference(photo: any): string | undefined {
  if (typeof photo?.name === "string" && photo.name) return photo.name;
  if (typeof photo?.getURI === "function") {
    return photo.getURI({ maxWidth: 400, maxHeight: 400 });
  }
  return undefined;
}

function coordinate(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

async function searchByText(request: Record<string, unknown>): Promise<any[]> {
  const places = placesLibrary();
  if (typeof places?.Place?.searchByText !== "function") {
    throw new Error("Google Places did not load in this tab");
  }
  const result = await places.Place.searchByText(request);
  return result?.places ?? [];
}

function toCompetitor(place: any, near: LatLng | null): Competitor {
  const latitude = coordinate(place.location?.lat?.());
  const longitude = coordinate(place.location?.lng?.());
  const located = latitude !== undefined && longitude !== undefined;
  return {
    id: place.id,
    name: place.displayName,
    imageUrl: photoReference(place.photos?.[0]),
    address: place.formattedAddress ?? undefined,
    latitude,
    longitude,
    distance:
      near && located
        ? calcCrow(near.lat, near.lng, latitude, longitude)
        : undefined,
  };
}

/**
 * Lodging matching `query`, biased around `near` when known. Text search
 * wants a name Google can match whole — "Nido" finds Nido Escondido, "Nid"
 * finds nothing — so this serves the nearby-lodging list; typed input goes
 * through `suggestLodging`. Nearest first.
 */
export async function searchLodging(
  query: string,
  near: LatLng | null,
): Promise<Competitor[]> {
  const places = await searchByText({
    textQuery: query,
    fields: COMPETITOR_FIELDS,
    includedType: "lodging",
    useStrictTypeFiltering: false,
    language: "en-US",
    ...(near ? { locationBias: near } : {}),
  });
  const found = places.map((place) => toCompetitor(place, near));
  if (near) {
    found.sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
  }
  return found;
}

/** The lodging around a point — what the customer picker shows before the
 *  customer types anything. */
export function nearbyLodging(near: LatLng): Promise<Competitor[]> {
  return searchLodging("hotel", near);
}

/**
 * Lodging matching what an operator has typed so far — autocomplete, which
 * answers partial names, with each suggestion resolved to the full place.
 * Kept in Google's order: its ranking already weighs `near`, and the
 * distance on each row says the rest. Falls back to text search on a Maps
 * build without the autocomplete class.
 */
export async function suggestLodging(
  input: string,
  near: LatLng | null,
): Promise<Competitor[]> {
  const places = placesLibrary();
  const autocomplete = places?.AutocompleteSuggestion;
  if (typeof autocomplete?.fetchAutocompleteSuggestions !== "function") {
    return searchLodging(input, near);
  }
  const { suggestions } = await autocomplete.fetchAutocompleteSuggestions({
    input,
    includedPrimaryTypes: ["lodging"],
    ...(near ? { locationBias: near } : {}),
  });
  const resolved: Array<Competitor | null> = await Promise.all(
    (suggestions ?? []).slice(0, MAX_SUGGESTIONS).map(async (suggestion) => {
      const place = suggestion?.placePrediction?.toPlace?.();
      if (!place) return null;
      await place.fetchFields({ fields: COMPETITOR_FIELDS });
      return toCompetitor(place, near);
    }),
  );
  return resolved.filter((c): c is Competitor => c !== null);
}

/** Where a lodging called `name` is — the fallback when the hotel's record
 *  carries no coordinates of its own. */
export async function locateLodging(name: string): Promise<LatLng | null> {
  if (!name.trim()) return null;
  const [place] = await searchByText({
    textQuery: name,
    fields: ["location"],
    includedType: "lodging",
    maxResultCount: 1,
  });
  const lat = coordinate(place?.location?.lat?.());
  const lng = coordinate(place?.location?.lng?.());
  return lat !== undefined && lng !== undefined ? { lat, lng } : null;
}
